import { Button, CollapsibleCard } from "@capacit-com/lara-law-shared-ui";
import { Edit2, Trash } from "lucide-react";
import { FC, useEffect, useState } from "react";
import UserList from "./UserList";
import { FeatureFlag, Group } from "@/models/feature/feature";
import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useGroups from "@/hooks/useGroups";
import useUsers from "@/hooks/useUsers";
import useFeatureStore from "@/stores/feature.store";
import { User } from "@/models/users/User";
import EditFeature from "./EditFeature";
import FeatureSkeleton from "./FeatureSkeleton";
import LoadingWrapper from "@/components/state-handlers/LoadingOverlay";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { Permission } from "@/lib/permissions/Permission.enum";
import { logger } from "@/lib/logger";

/** Danish display labels for feature flag section headers, keyed by name prefix. */
const SECTION_LABELS: Record<string, string> = {
  Modules: "Værktøjer",
  Consolidation: "Konsolidering",
};

/** Optional relationship notes shown under a section header, keyed by name prefix. */
const SECTION_NOTES: Record<string, string> = {
  Consolidation:
    "En del af Retsinformation · kræver at Retsinformation er aktiveret",
};

const FeatureWrapper: FC = () => {
  const {
    useFetchTenantFeatureFlags,
    removeAllUsersFromFeatureFlag,
    removeGroupFromFeatureFlag,
    removeUserFromFeatureFlag,
  } = useFeatureFlags();
  const { useQueryAllTenantUsers } = useUsers();
  const { useGetGroups } = useGroups();
  const { setGroups } = useFeatureStore();
  const fetchedGroups = useGetGroups();

  useEffect(() => {
    if (fetchedGroups.isSuccess && fetchedGroups.data) {
      setGroups(fetchedGroups.data);
    }
  }, [fetchedGroups.data, fetchedGroups.isSuccess, setGroups]);

  const [group, setGroup] = useState<Group | null>(null);
  const [flag, setFlag] = useState<FeatureFlag | null>(null);
  const [openFlags, setOpenFlags] = useState<Record<string, boolean>>({});
  const [removingUsersFromFeature, setRemovingUsersFromFeature] = useState<
    Record<string, string[]>
  >({});
  const [removingGroupsFromFeature, setRemovingGroupsFromFeature] = useState<
    Record<string, string[]>
  >({});
  const [aggregatedFeatureFlags, setAggregatedFeatureFlags] = useState<
    Record<string, FeatureFlag[]> | undefined
  >(undefined);

  const [tenantUsers, setTenantUsers] = useState<User[]>([]);
  const [
    isDeleteAllSpecificUsersDialogOpen,
    setIsDeleteAllSpecificUsersDialogOpen,
  ] = useState(false);
  const [isDeleteGroupDialogOpen, setIsDeleteGroupDialogOpen] = useState(false);

  const fetchedFeatureFlags = useFetchTenantFeatureFlags();
  const fetchedTenantUsers = useQueryAllTenantUsers();

  const aggregateFeatureFlags = (featureFlags: FeatureFlag[]) => {
    const groupedFeatureFlags: Record<string, FeatureFlag[]> = {};
    featureFlags.forEach((featureFlag) => {
      const prefix = featureFlag.name.split("-")[0];
      if (!groupedFeatureFlags[prefix]) {
        groupedFeatureFlags[prefix] = [];
      }
      groupedFeatureFlags[prefix].push(featureFlag);
    });

    return groupedFeatureFlags;
  };

  const handleToggleFeature = (strategyId: string) => {
    setOpenFlags((prev) => ({
      ...prev,
      [strategyId]: !prev[strategyId],
    }));
  };

  const handleRemoveAllSpecificUsersFromFeature = async (
    strategyId: string,
    featureName: string
  ) => {
    try {
      setIsDeleteAllSpecificUsersDialogOpen(false);
      setRemovingUsersFromFeature((prev) => ({
        ...prev,
        [strategyId]: tenantUsers.map((user) => user.id),
      }));
      await removeAllUsersFromFeatureFlag.mutateAsync({
        strategyId,
        featureName,
      });

      setTimeout(() => {
        setRemovingUsersFromFeature((prev) => ({
          ...prev,
          [strategyId]: [],
        }));
      }, 1000);
    } catch (error) {
      logger.error("Failed to remove all users from feature flag", {
        operation: "removeAllUsersFromFeatureFlag",
        error,
        featureName,
        strategyId,
      });
      showErrorToast(
        "Der opstod en fejl under fjernelse af brugere fra feature flag."
      );
      setRemovingUsersFromFeature((prev) => ({
        ...prev,
        [strategyId]: [],
      }));
    }
  };

  const handleRemoveGroupFromFeature = async (
    strategyId: string,
    groupId: string,
    featureName: string
  ) => {
    try {
      setIsDeleteGroupDialogOpen(false);
      setRemovingGroupsFromFeature((prev) => ({
        ...prev,
        [strategyId]: [...(prev[strategyId] || []), groupId],
      }));

      await removeGroupFromFeatureFlag.mutateAsync({
        strategyId,
        groupId,
        featureName,
      });

      setTimeout(() => {
        setRemovingGroupsFromFeature((prev) => ({
          ...prev,
          [strategyId]: prev[strategyId].filter((id) => id !== groupId),
        }));
      }, 1000);
    } catch (error) {
      logger.error("Failed to remove group from feature flag", {
        operation: "removeGroupFromFeatureFlag",
        error,
        featureName,
        strategyId,
        groupId,
      });
      showErrorToast(
        "Der opstod en fejl under fjernelse af gruppen fra feature flag."
      );
      setRemovingGroupsFromFeature((prev) => ({
        ...prev,
        [strategyId]: (prev[strategyId] || []).filter((id) => id !== groupId),
      }));
    }
  };

  const handleRemoveSpecificUserFromFeature = async (
    strategyId: string,
    userId: string,
    featureName: string
  ) => {
    try {
      setRemovingUsersFromFeature((prev) => ({
        ...prev,
        [strategyId]: [...(prev[strategyId] || []), userId],
      }));
      await removeUserFromFeatureFlag.mutateAsync({
        strategyId,
        userId,
        featureName,
      });

      setTimeout(() => {
        setRemovingUsersFromFeature((prev) => ({
          ...prev,
          [strategyId]: prev[strategyId].filter((id) => id !== userId),
        }));
      }, 1000);
    } catch (error) {
      logger.error("Failed to remove user from feature flag", {
        operation: "removeUserFromFeatureFlag",
        error,
        featureName,
        strategyId,
        userId,
      });
      showErrorToast(
        "Der opstod en fejl under fjernelse af brugeren fra feature flag."
      );
      setRemovingUsersFromFeature((prev) => ({
        ...prev,
        [strategyId]: (prev[strategyId] || []).filter((id) => id !== userId),
      }));
    }
  };

  const getFlagName = (flag: FeatureFlag) => {
    const flagName = flag.description.split(":")[1];
    return flagName ? flagName.trim() : flag.name;
  };

  useEffect(() => {
    if (fetchedFeatureFlags.isSuccess && fetchedFeatureFlags.data) {
      setAggregatedFeatureFlags(
        aggregateFeatureFlags(fetchedFeatureFlags.data)
      );
    }
  }, [fetchedFeatureFlags.data, fetchedFeatureFlags.isSuccess]);

  useEffect(() => {
    if (fetchedTenantUsers.isSuccess && fetchedTenantUsers.data) {
      setTenantUsers(fetchedTenantUsers.data);
    }
  }, [fetchedTenantUsers.data, fetchedTenantUsers.isSuccess]);

  return (
    <div>
      {fetchedFeatureFlags.isLoading && aggregatedFeatureFlags === undefined ? (
        <FeatureSkeleton />
      ) : (
        <>
          {aggregatedFeatureFlags &&
            Object.entries(aggregatedFeatureFlags).map(([prefix, flags]) => (
              <div key={prefix} className="mb-4">
                <h2 className="text-lg font-semibold mb-2">
                  {SECTION_LABELS[prefix] ?? prefix}
                </h2>
                {SECTION_NOTES[prefix] && (
                  <p className="text-sm text-muted-foreground mb-2">
                    {SECTION_NOTES[prefix]}
                  </p>
                )}
                {flags.map((currentFlag) => {
                  const isOpen = openFlags[currentFlag.strategyId] ?? false;
                  return (
                    <CollapsibleCard
                      key={currentFlag.strategyId}
                      title={getFlagName(currentFlag)}
                      isOpen={isOpen}
                      onToggle={() =>
                        handleToggleFeature(currentFlag.strategyId)
                      }
                      className="mb-2"
                      headerActions={
                        <PermissionGate
                          allOf={[Permission.UpdateUserFeatureFlag]}
                        >
                          <EditFeature
                            users={tenantUsers}
                            trigger={
                              <Button variant="ghost" size="sm" className="p-2">
                                <Edit2 className="size-4" />
                              </Button>
                            }
                            feature={currentFlag}
                          />
                        </PermissionGate>
                      }
                    >
                      {currentFlag.users.length === 0 &&
                      currentFlag.groups.length === 0 ? (
                        <div className="flex flex-col justify-center items-center h-full mt-4">
                          <p className="text-sm text-muted-foreground flex justify-center items-center h-full">
                            Alle har adgang
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4 mt-4 border-none">
                          {currentFlag.users.length > 0 && (
                            <div
                              key="feature-specificUsers"
                              className="rounded-lg shadow-[0px_2px_8px_2px_rgba(0,0,0,0.1)]"
                            >
                              <div className="flex flex-row justify-between items-center p-2 border-b">
                                <h3 className="text-sm font-normal">
                                  Specifikke brugere
                                </h3>
                                <PermissionGate
                                  allOf={[Permission.UpdateUserFeatureFlag]}
                                >
                                  <div className="flex flex-row">
                                    <EditFeature
                                      users={tenantUsers}
                                      feature={currentFlag}
                                      trigger={
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="p-2"
                                        >
                                          <Edit2 className="size-4" />
                                        </Button>
                                      }
                                      prefixedActiveTab="specificUsers"
                                    />

                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="p-2"
                                      onClick={() => {
                                        setFlag(currentFlag);
                                        setIsDeleteAllSpecificUsersDialogOpen(
                                          true
                                        );
                                      }}
                                    >
                                      <Trash className="size-4" />
                                    </Button>
                                  </div>
                                </PermissionGate>
                              </div>
                              <UserList
                                users={currentFlag.users}
                                removingUsers={
                                  removingUsersFromFeature[
                                    currentFlag.strategyId
                                  ] || []
                                }
                                onRemoveUser={async (userId) =>
                                  await handleRemoveSpecificUserFromFeature(
                                    currentFlag.strategyId,
                                    userId,
                                    currentFlag.name
                                  )
                                }
                                className="max-h-[40vh] min-h-[20vh] lg:max-h-[30vh] my-2"
                              />
                            </div>
                          )}
                          {currentFlag.groups.map((currentGroup) => (
                            <div
                              key={currentGroup.id}
                              className="rounded-lg shadow-[0px_2px_8px_2px_rgba(0,0,0,0.1)] relative"
                            >
                              {removingGroupsFromFeature[
                                currentFlag.strategyId
                              ]?.includes(currentGroup.id) && (
                                <LoadingWrapper text="Fjerner gruppe..." />
                              )}
                              <div className="flex flex-row justify-between items-center p-2 border-b">
                                <h3 className="text-sm font-normal">
                                  {currentGroup.name}
                                </h3>
                                <div className="flex flex-row">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="p-2"
                                    onClick={() => {
                                      setIsDeleteGroupDialogOpen(true);
                                      setFlag(currentFlag);
                                      setGroup(currentGroup);
                                    }}
                                  >
                                    <Trash className="size-4" />
                                  </Button>
                                </div>
                              </div>
                              <UserList
                                users={tenantUsers.filter((user) =>
                                  currentGroup.userIds.includes(user.id)
                                )}
                                className="max-h-[40vh] min-h-[20vh] lg:max-h-[30vh] my-2"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </CollapsibleCard>
                  );
                })}
              </div>
            ))}
        </>
      )}
      <ConfirmationDialog
        title={`Fjern alle specifikke brugere fra ${flag?.name} featuren`}
        description={`Du er i gang med at fjerne alle specifikke brugere fra ${flag?.name}, er du sikker på du vil fortsætte?\n\nVed at fjerne denne gruppe, vil alle medlemmer miste adgang til ${flag?.name} featuren.`}
        onConfirm={() =>
          handleRemoveAllSpecificUsersFromFeature(flag!.strategyId, flag!.name)
        }
        isOpen={isDeleteAllSpecificUsersDialogOpen}
        onClose={() => setIsDeleteAllSpecificUsersDialogOpen(false)}
        confirmText="Slet"
        confirmIcon={<Trash className="size-4" />}
        variant="destructive"
      />
      <ConfirmationDialog
        title={`Fjern gruppen ${group?.name} fra ${flag?.name} featuren`}
        description={`Du er i gang med at fjerne gruppen ${group?.name} fra ${flag?.name}, er du sikker på du vil fortsætte?\n\nVed at fjerne denne gruppe, vil alle medlemmer miste adgang til ${flag?.name} featuren.`}
        onConfirm={() =>
          handleRemoveGroupFromFeature(flag!.strategyId, group!.id, flag!.name)
        }
        isOpen={isDeleteGroupDialogOpen}
        onClose={() => setIsDeleteGroupDialogOpen(false)}
        confirmText="Slet"
        confirmIcon={<Trash className="size-4" />}
        variant="destructive"
      />
    </div>
  );
};

export default FeatureWrapper;
